import { createLogic, StandardAction } from "redux-logic";
import * as UtilMethods from "../../../utils/UtilMethods";
import { setError, setLoadingStatus } from "../reducers/applicationStateReducer";
import {
  addPreviousHandledBox,
  resetCurrentAppState,
  setIsBoxSwapFinished,
  setIsForcedRxBagVerification,
  setIsPartialDeliveryModalOpen,
  setIsVerifyPickModalOpen,
  setPickActionTaken,
} from "../reducers/controlAppStateReducer";

import { setAFrameAction, toggleScanDisabled } from "../reducers/aframeModeReducer";
import AFrameConstants from "../../aframeboxlookup/Constants";
import { CancellationReason } from "../../rxAutoMessages/rxAutoMessages";
import SortBoxEndpoints from "../../sortbox/SortBoxEndpoints";
import {
  // SET_AFRAME_BOX,
  SET_CONVEYOR_BOX,
  FETCH_CONVEYOR_BOX,
  setConveyorBoxData,
  setIPackBoxBarcode,
  UPDATE_BOX_BARCODE,
  TRIGGER_POST_PICK,
  TRIGGER_BATCH_PICK,
  resetCurrentReview,
  FINISH_SKU_PUTAWAY,
  fetchConveyorBox,
  REQUEST_NEW_SHIPPING_LABEL,
  requestNewShippingLabelFailed,
  CHANGE_SHIPPING_LABEL_TO_POSTEN,
  PRINT_NEW_LABEL,
  SET_BOX_AS_CONTROLLED,
  LOAD_IPACK_IMAGES,
  setImageData,
  RELOCATE_BOX,
  CONFIRM_ITEM_REMOVAL,
  CONFIRM_VERIFY_PICK_FINISHED,
  updateBoxItems,
  REFRESH_CONVEYOR_BOX,
  // verifyRxBagsRequired,
} from "../reducers/conveyorBoxReviewReducer";
import { PayloadWrapper } from "../types/PayloadWrapper";
import { conveyorBoxEndpoints } from "../../conveyorbox/endpoints";
import {
  IConveyorBox,
  IConveyorBoxItem,
  IConveyorBoxItemUI,
  IConveyorBoxUI,
  IRestockItemUI,
  SkuStock,
  UpdatePickedQuantityPayload,
} from "../../interfaces/ConveyorBox/IConveyorBox";
import { setIsLoading } from "../reducers/loadingStateReducer";
import { ArgumentAction } from "redux-logic/definitions/action";
import warehouseMessagesClient from "../../../utils/WarehouseMessagesClient";
import {
  addMessage,
  MessageType,
  clearMessages,
  RemoveMessageAction,
  REMOVE_MESSAGE,
  IResponseMessageUI,
  RestockReason,
} from "../reducers/responseMessagesReducer";
import {
  getStructuredBoxItems,
  isBoxItemVerified,
  isPickedItem,
} from "../../../utils/helpers/conveyor-box";
import { clearCancelledItems, setCancelledItems } from "../reducers/cancelledItemReducer";
import {
  triggerForcedRestockStage,
  triggerResetStage,
  triggerRestockCancelledItemsStage,
} from "../reducers/globalStateReducer";
import { RootState } from "../storeConfig";
import constants from "../../aframeboxlookup/Constants";
import { LIMA_WORKSTATIONS } from "../../../utils/Constants";
import { resetRestockState, setRestockItems } from "../reducers/restockReducer";

const { ActionStates, LookupValueOrigin } = AFrameConstants;

const fetchAndDisplayWarehouseMessages = async (
  dispatch: any,
  orderNumber: number,
  workstationId: number
) => {
  try {
    const messages = await warehouseMessagesClient.query(
      1, // entityType - Order
      orderNumber // entityId - order number
    );

    if (messages && messages.length > 0) {
      console.log(`Found ${messages.length} warehouse messages for order ${orderNumber}`);

      messages.forEach((msg: any) => {
        dispatch(
          addMessage({
            messageType: MessageType.WAREHOUSE,
            title: `Order ${orderNumber} Alert`,
            content: msg.message || msg.content || msg.text || "Unknown warehouse message",
            serverMessageId: msg.id,
          })
        );
      });
    } else {
      console.log(`No warehouse messages found for order ${orderNumber}`);
    }
  } catch (error) {
    console.error("Failed to fetch warehouse messages for order:", orderNumber, error);
  }
};

const fetchIPackImagesLogic = createLogic({
  type: LOAD_IPACK_IMAGES,
  async process(
    {
      getState,
    }: {
      getState: any;
    },
    dispatch,
    done
  ) {
    const { conveyorBoxReducer } = getState();
    await conveyorBoxEndpoints.loadContentImage(conveyorBoxReducer.boxBarcode).then((result) => {
      if (conveyorBoxReducer.boxBarcode === getState().boxReviewReducer?.currentBox?.boxBarcode) {
        dispatch(setImageData({ imageBytes: result.imageBytes, imageFound: result.imageFound }));
      }
    });

    done();
  },
});

const resetViewAndAddBoxAsHandled = (
  dispatch: <T extends ArgumentAction>(action: T) => T,
  state: any
) => {
  if (state.conveyorBoxReviewReducer.boxBarcode?.length > 0) {
    dispatch(addPreviousHandledBox(new PayloadWrapper(state.conveyorBoxReviewReducer.boxBarcode)));
  }
  dispatch(resetCurrentReview());
};

const confirmVerifyPickFinished = createLogic({
  type: CONFIRM_VERIFY_PICK_FINISHED,
  async process(
    {
      getState,
    }: {
      getState: any;
    },
    dispatch,
    done
  ) {
    const state = getState();
    const conveyorBox: IConveyorBoxUI = state.conveyorBoxReviewReducer;
    const { otc, bundles } = getStructuredBoxItems(conveyorBox.items.active);

    const updatedItems = [...conveyorBox.items.active];

    for (let bundle of bundles) {
      const isFinishedBundle = isPickedItem(bundle);
      // first we check that every finished bundle (sealed rx bag) has been verified. Bundles can't be post/batch picked.
      if (isFinishedBundle) {
        const isVerifiedBundle = isBoxItemVerified(bundle);
        if (isVerifiedBundle) continue;
        dispatch(
          addMessage({
            messageType: MessageType.WARNING,
            title: "Ogiltig verifiering",
            content:
              "Icke verifierade RX-påsar förkommer - vänligen verifiera alla rx-påsar innan ni går vidare.",
          })
        );
        return;
      } else {
        for (let item of bundle.items) {
          const isItemVerified = isBoxItemVerified(item);
          if (isItemVerified) continue;
          const { verifiedQuantity, pickedQuantity } = item;
          const quantityMissing = pickedQuantity - verifiedQuantity;
          const itemIdx = updatedItems.findIndex(
            (itemToBeUpdated) => itemToBeUpdated.id === item.id
          );
          updatedItems[itemIdx] = {
            ...updatedItems[itemIdx],
            adjustedPickedQuantity: pickedQuantity - quantityMissing,
            verifiedQuantity: pickedQuantity,
          };
        }
      }
    }
    for (let item of otc) {
      const isItemVerified = isBoxItemVerified(item);
      if (isItemVerified) continue;
      const { verifiedQuantity, pickedQuantity } = item;
      const quantityMissing = pickedQuantity - verifiedQuantity;
      const itemIdx = updatedItems.findIndex((itemToBeUpdated) => itemToBeUpdated.id === item.id);
      updatedItems[itemIdx] = {
        ...updatedItems[itemIdx],
        adjustedPickedQuantity: pickedQuantity - quantityMissing,
        verifiedQuantity: pickedQuantity,
      };
    }

    dispatch(
      updateBoxItems(
        new PayloadWrapper({ active: updatedItems, cancelled: conveyorBox.items.cancelled })
      )
    );

    done();
  },
});

const fetchConveyorBoxLogic = createLogic({
  type: FETCH_CONVEYOR_BOX,
  async process(
    {
      getState,
      action,
    }: {
      getState: any;
      action: StandardAction<
        string,
        {
          type: number;
          input: string;
          errorCallback: (input: string) => void;
          callbackFn: () => void;
        }
      >;
    },
    dispatch,
    done
  ) {
    console.log(action.payload);
    const state = getState();
    dispatch(resetCurrentReview());
    dispatch(resetCurrentAppState());
    dispatch(clearMessages());
    dispatch(setPickActionTaken({ value: false }));
    dispatch(setLoadingStatus(new PayloadWrapper(true)));
    dispatch(toggleScanDisabled(new PayloadWrapper(true)));
    // dispatch(setIPackBoxBarcode(new PayloadWrapper(action.payload.input)));

    const enteredBarcode = action.payload.input;
    try {
      let workstationId = UtilMethods.getParamValue("ws");
      const data: IConveyorBox = await conveyorBoxEndpoints.getConveyorBox(
        action.payload.type,
        enteredBarcode,
        workstationId
      );

      data.items.active = data.items.active.map((item) => {
        return { ...item, adjustedPickedQuantity: item.pickedQuantity, verifiedQuantity: 0 };
      }) as any;

      const isContainingRxBags = data.items.active.some((item) => {
        return (
          (item.attributes.includes("RX") && item.confirmedQuantity > 0) ||
          (item.attributes.includes("RXBAG") && item.confirmedQuantity > 0)
        );
      });
      if (
        isContainingRxBags &&
        [LIMA_WORKSTATIONS["VBG-CHK-FIN1"], LIMA_WORKSTATIONS["VBG-CHK-FIN2"]].includes(
          parseInt(workstationId)
        )
      ) {
        dispatch(setIsForcedRxBagVerification(new PayloadWrapper(true)));
      }

      // STORE CANCELLED ITEMS
      // make sure cancelled items are handled first
      const cancelledItems = data.items.cancelled;
      const cancelledRx = cancelledItems.filter(({ attributes }) => attributes.includes("RX"));

      if (cancelledRx.length > 0) {
        const cancelledItemsTransformed = cancelledRx.map((item) => {
          return { ...item, cancelledQuantityVerified: 0, isRestocked: false };
        });
        dispatch(setCancelledItems(cancelledItemsTransformed));
        dispatch(triggerRestockCancelledItemsStage());
      }

      dispatch(
        setConveyorBoxData({
          conveyorBoxReview: data,
        })
      );

      const reviewParam = UtilMethods.getParamValue("review");
      const isReviewMode = (reviewParam && reviewParam === "true") || reviewParam === "1";
      if (!isReviewMode) dispatch(setIsVerifyPickModalOpen(new PayloadWrapper(true)));

      if (data.orderNumber) {
        await fetchAndDisplayWarehouseMessages(dispatch, data.orderNumber, workstationId);
      }

      action.payload?.callbackFn?.();

      dispatch(setLoadingStatus(new PayloadWrapper(false)));
      dispatch(toggleScanDisabled(new PayloadWrapper(false)));

      // get stock
      let items = [...data.items.active, ...data.items.cancelled];
      items = items.filter((i: any) => !i.attributes.includes("RXBAG")).map((i: any) => i.sku.id);
      try {
        dispatch(setIsLoading({ key: "isFetchingStockData", value: true }));
        let stockInformationList: SkuStock[] = await conveyorBoxEndpoints.getStock(
          enteredBarcode,
          items
        );

        const stockMap = new Map(stockInformationList.map((stock) => [stock.skuID, stock]));

        const updatedBoxItems = data.items.active.map((item) => {
          const matchingStock = stockMap.get(item.sku.id);

          if (!matchingStock) return item;

          matchingStock.isSuccess = true; // at the time of building the quick-fix, the API returned false for the isSuccess property so i'm overruling it here

          return {
            ...item,
            sku: {
              ...item.sku,
              stock: matchingStock,
            },
          };
        });
        dispatch(
          updateBoxItems(
            new PayloadWrapper({ active: updatedBoxItems, cancelled: data.items.cancelled })
          )
        );
      } catch (error) {
        dispatch(
          addMessage({
            messageType: MessageType.ERROR,
            title: "Hoppsan!",
            content: "Något gick fel vid hämtning av lagersaldo - möjligt fel i WAMAS",
          })
        );
      } finally {
        dispatch(setIsLoading({ key: "isFetchingStockData", value: false }));
      }
      done();
    } catch (error: any) {
      console.error(error);

      dispatch(setLoadingStatus(new PayloadWrapper(false)));
      dispatch(toggleScanDisabled(new PayloadWrapper(false)));
      resetViewAndAddBoxAsHandled(dispatch, state);

      dispatch(
        addMessage({
          messageType: MessageType.ERROR,
          title: "",
          content: error.message,
        })
      );
      action.payload?.errorCallback?.(error);
      done();
    }
  },
});

const refreshConveyorBoxLogic = createLogic({
  type: REFRESH_CONVEYOR_BOX,
  async process(
    {
      getState,
      action,
    }: {
      getState: any;
      action: StandardAction<
        string,
        {
          callbackFn: () => void;
        }
      >;
    },
    dispatch,
    done
  ) {
    const state: RootState = getState();
    // dispatch(resetCurrentReview());
    // dispatch(resetCurrentAppState());
    // dispatch(clearMessages());
    // dispatch(setPickActionTaken({ value: false }));
    dispatch(setLoadingStatus(new PayloadWrapper(true)));
    dispatch(toggleScanDisabled(new PayloadWrapper(true)));
    dispatch(triggerResetStage());
    dispatch(clearCancelledItems());
    dispatch(resetRestockState());
    // dispatch(setIPackBoxBarcode(new PayloadWrapper(action.payload.input)));

    try {
      let workstationId = UtilMethods.getParamValue("ws");
      const data: IConveyorBox = await conveyorBoxEndpoints.getConveyorBox(
        LookupValueOrigin.ScannedBarcode,
        state.conveyorBoxReviewReducer.boxBarcode,
        workstationId
      );

      data.items.active = data.items.active.map((item) => {
        return { ...item, adjustedPickedQuantity: item.pickedQuantity, verifiedQuantity: 0 };
      }) as any;

      const isContainingRxBags = data.items.active.some((item) => {
        return (
          (item.attributes.includes("RX") && item.confirmedQuantity > 0) ||
          (item.attributes.includes("RXBAG") && item.confirmedQuantity > 0)
        );
      });

      if (
        isContainingRxBags &&
        [LIMA_WORKSTATIONS["VBG-CHK-FIN1"], LIMA_WORKSTATIONS["VBG-CHK-FIN2"]].includes(
          parseInt(workstationId)
        )
      ) {
        dispatch(setIsForcedRxBagVerification(new PayloadWrapper(true)));
      }

      // STORE CANCELLED ITEMS
      // make sure cancelled items are handled first
      const cancelledItems = data.items.cancelled;
      const cancelledRx = cancelledItems.filter(({ attributes }) => attributes.includes("RX"));

      if (cancelledRx.length > 0) {
        const cancelledItemsTransformed = cancelledRx.map((item) => {
          return { ...item, cancelledQuantityVerified: 0, isRestocked: false };
        });
        dispatch(setCancelledItems(cancelledItemsTransformed));
        dispatch(triggerRestockCancelledItemsStage());
      }

      dispatch(
        setConveyorBoxData({
          conveyorBoxReview: data,
        })
      );

      const reviewParam = UtilMethods.getParamValue("review");
      const isReviewMode = (reviewParam && reviewParam === "true") || reviewParam === "1";
      if (!isReviewMode) dispatch(setIsVerifyPickModalOpen(new PayloadWrapper(true)));

      if (data.orderNumber) {
        await fetchAndDisplayWarehouseMessages(dispatch, data.orderNumber, workstationId);
      }
      action.payload?.callbackFn();

      dispatch(setLoadingStatus(new PayloadWrapper(false)));
      dispatch(toggleScanDisabled(new PayloadWrapper(false)));

      // get stock
      try {
        dispatch(setIsLoading({ key: "isFetchingStockData", value: true }));
        const stockInformationList: SkuStock[] = await conveyorBoxEndpoints.getStock(
          state.conveyorBoxReviewReducer.boxBarcode
        );
        for (const stock of stockInformationList) {
          const updatedBoxItems = data.items.active.map((i) =>
            i.sku.id === stock.skuID ? { ...i, sku: { ...i.sku, stock: stock } } : i
          );
          dispatch(
            updateBoxItems(
              new PayloadWrapper({ active: updatedBoxItems, cancelled: data.items.cancelled })
            )
          );
          dispatch(setIsLoading({ key: "isFetchingStockData", value: false }));
        }
      } catch (error) {
        dispatch(setIsLoading({ key: "isFetchingStockData", value: false }));
        dispatch(
          addMessage({
            messageType: MessageType.ERROR,
            title: "Hoppsan!",
            content: "Något gick fel vid hämtning av lagersaldo - möjligt fel i WAMAS",
          })
        );
      }
      done();
    } catch (error: any) {
      console.error(error);

      dispatch(setLoadingStatus(new PayloadWrapper(false)));
      dispatch(toggleScanDisabled(new PayloadWrapper(false)));
      resetViewAndAddBoxAsHandled(dispatch, state);

      dispatch(
        addMessage({
          messageType: MessageType.ERROR,
          title: "",
          content: error.message,
        })
      );
      done();
    }
  },
});

const updateBoxBarcodeLogic = createLogic({
  type: UPDATE_BOX_BARCODE,
  async process(
    {
      getState,
      action,
    }: {
      getState: any;
      action: StandardAction<
        string,
        {
          currentBarcode: string;
          newBarcode: string;
          selectedBoxSize: string;
          errorCallbackFn: (message: string) => void;
        }
      >;
    },
    dispatch,
    done
  ) {
    const { currentBarcode, newBarcode, selectedBoxSize } = action.payload;
    const state = getState();
    const appState = state.applicationStateReducer;
    dispatch(setIsLoading({ key: "isPendingBoxSwap", value: true }));
    dispatch(toggleScanDisabled(new PayloadWrapper(true)));
    try {
      await conveyorBoxEndpoints.updateBoxBarcode(currentBarcode, newBarcode, selectedBoxSize);
      dispatch(
        addMessage({
          messageType: MessageType.SUCCESS,
          title: newBarcode,
          content: `Ny streckkod för lådan: ${currentBarcode}. Ny: ${newBarcode}`,
        })
      );

      dispatch(setIPackBoxBarcode(new PayloadWrapper(newBarcode)));
      dispatch(setIsLoading({ key: "isPendingBoxSwap", value: false }));
      dispatch(setIsBoxSwapFinished(new PayloadWrapper(true)));

      done();
    } catch (error: any) {
      console.error(error);
      dispatch(toggleScanDisabled(new PayloadWrapper(false)));
      dispatch(setIsLoading({ key: "isPendingBoxSwap", value: false }));
      dispatch(
        addMessage({
          messageType: MessageType.ERROR,
          title: currentBarcode,
          content: error.message,
        })
      );
      done();
    }
  },
});

const triggerPostPickLogic = createLogic({
  type: TRIGGER_POST_PICK,
  async process(
    {
      getState,
      action,
    }: {
      getState: any;
      action: StandardAction<string, {}>;
    },
    dispatch,
    done
  ) {
    dispatch(setIsLoading({ key: "isPendingPostPick", value: true }));
    dispatch(toggleScanDisabled(new PayloadWrapper(true)));
    const state = getState();

    const conveyorBox: IConveyorBoxUI = state.conveyorBoxReviewReducer;
    const currentBarcode = conveyorBox.boxBarcode;
    const missingItems = conveyorBox.items.active
      .filter((item) => item.adjustedPickedQuantity < item.pickedQuantity)
      .map(({ id, pickedQuantity, adjustedPickedQuantity }: any) => {
        return {
          orderLogisticsId: id, // todo: should be generic, not orderLogisticsId
          pickQuantity: pickedQuantity - adjustedPickedQuantity,
          newOrderQuantity: -1,
        };
      });
    const data = {
      boxBarcode: currentBarcode,
      items: missingItems,
    };
    try {
      await conveyorBoxEndpoints.triggerPostPick(currentBarcode, data);
      dispatch(setIsLoading({ key: "isPendingPostPick", value: false }));
      dispatch(setPickActionTaken({ value: true }));
      dispatch(
        addMessage({
          messageType: MessageType.SUCCESS,
          title: currentBarcode,
          content: "Placera lådan på bandet",
        })
      );
      resetViewAndAddBoxAsHandled(dispatch, state);
      done();
    } catch (error: any) {
      console.error(error);
      dispatch(toggleScanDisabled(new PayloadWrapper(false)));
      dispatch(setIsLoading({ key: "isPendingPostPick", value: false }));
      dispatch(
        addMessage({
          messageType: MessageType.ERROR,
          title: currentBarcode,
          content: error.message,
        })
      );
      done();
    }
  },
});

const triggerBatchPickLogic = createLogic({
  type: TRIGGER_BATCH_PICK,
  async process(
    {
      getState,
      action,
    }: {
      getState: any;
      action: StandardAction<string, {}>;
    },
    dispatch,
    done
  ) {
    dispatch(setIsLoading({ key: "isPendingBatchPick", value: true }));
    dispatch(toggleScanDisabled(new PayloadWrapper(true)));
    const state = getState();
    const conveyorBox: IConveyorBoxUI = state.conveyorBoxReviewReducer;

    const currentBarcode = conveyorBox.boxBarcode;
    const missingItems = conveyorBox.items.active
      .filter((item) => item.adjustedPickedQuantity < item.pickedQuantity)
      .map(({ id, pickedQuantity, adjustedPickedQuantity }: any) => {
        return {
          orderLogisticsId: id,
          pickQuantity: pickedQuantity - adjustedPickedQuantity,
        };
      });
    const data = {
      boxBarcode: currentBarcode,
      items: missingItems,
    };
    try {
      await conveyorBoxEndpoints.triggerBatchPick(currentBarcode, data);
      // dispatch(setIPackBoxBarcode(new PayloadWrapper(newBarcode)));
      dispatch(setIsLoading({ key: "isPendingBatchPick", value: false }));
      dispatch(setPickActionTaken({ value: true }));
      dispatch(
        addMessage({
          messageType: MessageType.SUCCESS,
          title: currentBarcode,
          content: "Placera lådan på bandet",
        })
      );
      resetViewAndAddBoxAsHandled(dispatch, state);

      done();
    } catch (error: any) {
      console.error(error);
      dispatch(toggleScanDisabled(new PayloadWrapper(false)));
      dispatch(setIsLoading({ key: "isPendingBatchPick", value: false }));
      dispatch(
        addMessage({
          messageType: MessageType.ERROR,
          title: currentBarcode,
          content: error.message,
        })
      );
      done();
    }
  },
});

const relocateBoxLogic = createLogic({
  type: RELOCATE_BOX,
  async process(
    {
      getState,
      action,
    }: {
      getState: any;
      action: StandardAction<string, {}>;
    },
    dispatch,
    done
  ) {
    dispatch(setIsLoading({ key: "isRelocateBoxLoading", value: true }));
    dispatch(toggleScanDisabled(new PayloadWrapper(true)));
    const state = getState();

    let workstationId = UtilMethods.getParamValue("ws");
    const conveyorBox: IConveyorBoxUI = state.conveyorBoxReviewReducer;
    const currentBarcode = conveyorBox.boxBarcode;

    try {
      await conveyorBoxEndpoints.relocateBox(currentBarcode, workstationId);
      dispatch(
        addMessage({
          messageType: MessageType.SUCCESS,
          title: currentBarcode,
          content: "Lådans plats är nu uppdaterad.",
        })
      );
      dispatch(setIsLoading({ key: "isRelocateBoxLoading", value: false }));
      dispatch(
        addMessage({
          messageType: MessageType.SUCCESS,
          title: currentBarcode,
          content: `Låda förflyttad till station`,
        })
      );
      done();
    } catch (error: any) {
      console.error(error);
      dispatch(toggleScanDisabled(new PayloadWrapper(false)));
      dispatch(setIsLoading({ key: "isRelocateBoxLoading", value: false }));
      dispatch(
        addMessage({
          messageType: MessageType.ERROR,
          title: currentBarcode,
          content: error.message,
        })
      );
      done();
    }
  },
});

const finishSkuPutawayLogic = createLogic({
  type: FINISH_SKU_PUTAWAY,
  async process(
    {
      getState,
      action,
    }: {
      getState: any;
      action: StandardAction<
        string,
        {
          items: IConveyorBoxItem[] | null;
          isCancelledItems: boolean;
          selectedQuantities: number[];
        }
      >;
    },
    dispatch,
    done
  ) {
    dispatch(setIsLoading({ key: "isPendingSkuPutaway", value: true }));
    dispatch(toggleScanDisabled(new PayloadWrapper(true)));
    const state = getState();
    var data = action.payload;
    let workstationId = UtilMethods.getParamValue("ws");

    const conveyorBox: IConveyorBox = state.conveyorBoxReviewReducer;
    const currentBarcode = conveyorBox.boxBarcode;
    let payloadItems: any = [];

    if (!action.payload.isCancelledItems && data.items) {
      for (let skuId in data.items) {
        const { quantity }: any = data.items[skuId];

        console.log(skuId, quantity);
        payloadItems.push({ id: skuId, quantity: quantity });
      }
    } else {
      payloadItems = conveyorBox.items.cancelled.map((item) => {
        return {
          id: item.id,
          quantity: item.cancelledQuantity,
        };
      });
    }

    try {
      await conveyorBoxEndpoints.finishSkuPutaway(currentBarcode, {
        orderNumber: conveyorBox.orderNumber,
        items: payloadItems,
      });

      const payloadQtyById = new Map<string | number, number>(
        payloadItems.map((pi: any) => [Number(pi.id), pi.quantity ?? 0])
      );

      if ([LIMA_WORKSTATIONS["VBG-CHK-RX"]].includes(parseInt(workstationId))) {
        const restockItems: IRestockItemUI[] = conveyorBox.items.active
          .filter((i) => !i.attributes.includes("RXBAG"))
          .map((i) => {
            const payloadQuantity = payloadQtyById.get(i.id) ?? 0;

            const confirmedQuantity = Number(i.confirmedQuantity ?? 0);
            const pickedQuantity = Number(i.pickedQuantity ?? 0);

            const quantityToRestock = Math.max(
              pickedQuantity + payloadQuantity - confirmedQuantity,
              0
            );

            return {
              id: i.id,
              article: i.article,
              sku: i.sku,
              quantity: quantityToRestock,
              quantityVerified: 0,
              attributes: i.attributes,
              varunummer: i.varunummer,
              forcedShelfPrefix: "",
              operatorAssignedShelf: "",
              isRestocked: false,
              restockReason: RestockReason.PARTIAL_DELIVERY,
            };
          })

          .filter((ri) => ri.quantity > 0);
        dispatch(setRestockItems(restockItems));
        dispatch(triggerForcedRestockStage());
      }

      dispatch(
        addMessage({
          messageType: MessageType.SUCCESS,
          title: currentBarcode,
          content: "Vänligen hantera artiklarna och placera sedan lådan på bandet.",
        })
      );
      dispatch(setIsPartialDeliveryModalOpen(new PayloadWrapper(false)));
      dispatch(
        fetchConveyorBox({
          type: LookupValueOrigin.ScannedBarcode,
          input: currentBarcode,
        })
      );

      dispatch(setIsLoading({ key: "isPendingSkuPutaway", value: false }));

      done();
    } catch (error: any) {
      console.error(error);
      dispatch(toggleScanDisabled(new PayloadWrapper(false)));
      dispatch(setIsLoading({ key: "isPendingSkuPutaway", value: false }));
      dispatch(setIsPartialDeliveryModalOpen(new PayloadWrapper(false)));
      dispatch(
        addMessage({
          messageType: MessageType.ERROR,
          title: currentBarcode,
          content: error.message,
        })
      );
      done();
    }
  },
});

const confirmItemRemovaLogic = createLogic({
  type: CONFIRM_ITEM_REMOVAL,
  async process(
    {
      getState,
      action,
    }: {
      getState: any;
      action: StandardAction<string, { skuId: number }>;
    },
    dispatch,
    done
  ) {
    const state = getState();
    const reducer = state.conveyorBoxReviewReducer;
    const boxBarcode = reducer.boxBarcode;

    try {
      await conveyorBoxEndpoints.confirmItemRemoval(boxBarcode, action.payload.skuId);
      dispatch(
        fetchConveyorBox({
          type: LookupValueOrigin.ScannedBarcode,
          input: boxBarcode,
        })
      );
      // dispatch(
      //   setPickActionTaken({
      //     value: true,
      //   })
      // );
      // dispatch(
      //   addMessage({
      //     messageType: "SUCCESS",
      //     title: boxBarcode,
      //     content: "Placera lådan på bandet",
      //   })
      // );
      // resetViewAndAddBoxAsHandled(dispatch, state);
      done();
    } catch (error: any) {
      console.error(error);
      dispatch(
        addMessage({
          messageType: MessageType.ERROR,
          title: boxBarcode,
          content: error.message,
        })
      );
      done();
    }
  },
});

const setBoxAsControlledLogic = createLogic({
  type: SET_BOX_AS_CONTROLLED,
  async process(
    {
      getState,
      action,
    }: {
      getState: any;
      action: StandardAction<string>;
    },
    dispatch,
    done
  ) {
    const state = getState();
    console.log("🚀 ~ state:", state);
    const reducer = state.conveyorBoxReviewReducer;
    const adjustedPicks: UpdatePickedQuantityPayload[] = reducer.items.active.reduce(
      (
        acc: UpdatePickedQuantityPayload[],
        { id, pickedQuantity, adjustedPickedQuantity }: IConveyorBoxItemUI
      ) => {
        if (adjustedPickedQuantity === pickedQuantity) return acc;
        acc.push({
          itemId: id,
          quantity: Math.abs(adjustedPickedQuantity - pickedQuantity),
          action: adjustedPickedQuantity < pickedQuantity ? "Decrease" : "Increase",
        });
        return acc;
      },
      []
    );
    const boxBarcode = reducer.boxBarcode;

    try {
      await conveyorBoxEndpoints.setBoxAsControlled(boxBarcode, adjustedPicks);
      dispatch(
        setPickActionTaken({
          value: true,
        })
      );
      dispatch(
        addMessage({
          messageType: MessageType.SUCCESS,
          title: boxBarcode,
          content: "Placera lådan på bandet",
        })
      );
      resetViewAndAddBoxAsHandled(dispatch, state);
      done();
    } catch (error: any) {
      console.error(error);
      dispatch(
        addMessage({
          messageType: MessageType.ERROR,
          title: boxBarcode,
          content: error.message,
        })
      );
      done();
    }
  },
});

const changeShippingLabelToPosten = createLogic({
  type: CHANGE_SHIPPING_LABEL_TO_POSTEN,
  async process(
    {
      getState,
    }: {
      getState: any;
    },
    dispatch,
    done
  ) {
    const state = getState();
    const boxBarcode = state.conveyorBoxReviewReducer.boxBarcode;

    try {
      // 1. Call the new endpoint to change the shipping method to Posten (116) in the DB
      let result = await conveyorBoxEndpoints.changeShippingMethod(boxBarcode);

      if (result && result.length > 0) {
        dispatch(
          addMessage({
            messageType: MessageType.WARNING,
            title: boxBarcode,
            content: `Kunde inte ändra fraktsätt: ${result}`,
          })
        );
      } else {
        // 2. If successful (empty result), request the new label
        // The backend will now generate a label using the updated shipping method (116)
        await conveyorBoxEndpoints.requestNewShippingLabel(boxBarcode);

        dispatch(
          addMessage({
            messageType: MessageType.SUCCESS,
            title: boxBarcode,
            content: "Fraktetikett har ändrats till Posten och skickats till automationen.",
          })
        );
      }
      done();
    } catch (error: any) {
      console.error(error);
      dispatch(
        addMessage({
          messageType: MessageType.ERROR,
          title: boxBarcode,
          content: error.message || "Ett fel inträffade vid byte av fraktsätt.",
        })
      );
      done();
    }
  },
});

const requestNewShippingLabel = createLogic({
  type: REQUEST_NEW_SHIPPING_LABEL,
  async process(
    {
      getState,
      action,
    }: {
      getState: any;
      action: StandardAction<string>;
    },
    dispatch,
    done
  ) {
    const state = getState();

    const boxBarcode = state.conveyorBoxReviewReducer.boxBarcode;

    try {
      await conveyorBoxEndpoints.requestNewShippingLabel(boxBarcode);
      dispatch(
        addMessage({
          messageType: MessageType.SUCCESS,
          title: boxBarcode,
          content: "Ny fraktetikett skapad för lådan. Vänligen placera lådan innan lådförslutaren.",
        })
      );
      done();
    } catch (error: any) {
      console.error(error);
      dispatch(requestNewShippingLabelFailed());
      dispatch(
        addMessage({
          messageType: MessageType.ERROR,
          title: boxBarcode,
          content: error.message,
        })
      );
      done();
    }
  },
});

const printNewLabel = createLogic({
  type: PRINT_NEW_LABEL,
  async process(
    {
      getState,
      action,
    }: {
      getState: any;
      action: StandardAction<string>;
    },
    dispatch,
    done
  ) {
    const state = getState();

    const boxBarcode = state.conveyorBoxReviewReducer.boxBarcode;

    try {
      const shippingId = state.conveyorBoxReviewReducer.shipping.id;
      const shippingMethodId = state.conveyorBoxReviewReducer.shipping.shippingMethod;
      const cancelOldLabel = false;

      let result = await conveyorBoxEndpoints.printNewLabel(
        shippingId,
        shippingMethodId,
        cancelOldLabel
      );

      if (result.length > 0) {
        dispatch(
          addMessage({
            messageType: MessageType.WARNING,
            title: boxBarcode,
            content: `Något gick fel vid utleverans: ${result}`,
          })
        );
        done();

        return;
      }
      dispatch(
        addMessage({
          messageType: MessageType.SUCCESS,
          title: boxBarcode,
          content: "Ny fraktetikett utskriven.",
        })
      );
      done();
    } catch (error: any) {
      dispatch(
        addMessage({
          messageType: MessageType.ERROR,
          title: boxBarcode,
          content: error.exceptionMessage,
        })
      );
      done();
    }
  },
});

const removeMessageLogic = createLogic({
  type: REMOVE_MESSAGE,
  latest: true,
  async process({ getState, action }, dispatch, done) {
    const state: any = getState();
    const payload = (action as RemoveMessageAction).payload;
    const message =
      typeof payload === "object"
        ? (payload as IResponseMessageUI)
        : ((state.responseMessages || []) as IResponseMessageUI[]).find(
            (msg) => msg.id === payload
          );

    if (message?.messageType === MessageType.WAREHOUSE) {
      const ackId = message.serverMessageId ?? message.id;
      if (ackId == null) {
        console.warn("No acknowledgment id found for warehouse message", message);
        done();
        return;
      }
      try {
        await warehouseMessagesClient.acknowledge(ackId as number);
        console.log(`Acknowledged warehouse message ${ackId}`);
      } catch (error) {
        console.error("Failed to acknowledge warehouse message:", error);
      }
    }

    done();
  },
});

export {
  fetchIPackImagesLogic,
  fetchConveyorBoxLogic,
  refreshConveyorBoxLogic,
  updateBoxBarcodeLogic,
  triggerPostPickLogic,
  triggerBatchPickLogic,
  finishSkuPutawayLogic,
  confirmItemRemovaLogic,
  setBoxAsControlledLogic,
  relocateBoxLogic,
  requestNewShippingLabel,
  printNewLabel,
  changeShippingLabelToPosten,
  confirmVerifyPickFinished,
  removeMessageLogic,
};
